import React, { useEffect, useMemo, useState } from "react";
import { Monitor, CheckCircle2, PackageCheck, Send, Link as LinkIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const STATUS_CLS = {
  "Pending Contact": "bg-amber-50 text-amber-700 border-amber-200",
  "Contacted": "bg-sky-50 text-sky-700 border-sky-200",
  "Order Processing": "bg-violet-50 text-violet-700 border-violet-200",
  "Ordered": "bg-violet-50 text-violet-700 border-violet-200",
  "Ready/Shipped": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Cancelled": "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Equipment() {
  const { toast } = useToast();
  const [pricing, setPricing] = useState(null);
  const [requests, setRequests] = useState([]);
  const [mode, setMode] = useState("complete_setup"); // complete_setup | individual
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", shipping_address: "", city: "", state: "", zip: "", preferred_contact_method: "Either", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/equipment/pricing").then((r) => setPricing(r.data)).catch(() => {}),
    api.get("/equipment/requests").then((r) => setRequests(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const toggleItem = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const indivTotal = useMemo(() => {
    if (!pricing) return 0;
    return pricing.items.filter((i) => selected.includes(i.item_key)).reduce((a, i) => a + Number(i.price), 0);
  }, [pricing, selected]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "individual" && selected.length === 0) { toast({ title: "Select equipment", description: "Choose at least one item.", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const r = await api.post("/equipment/requests", { ...form, equipment_type: mode, selected_items: mode === "individual" ? selected : [] });
      toast({ title: "Equipment request submitted successfully.", description: r.data.confirmation_email_sent ? "A confirmation email is on its way. We'll contact you soon." : "Campbell Communications will contact you regarding your request." });
      setSelected([]); setMode("complete_setup");
      await load();
    } catch (err) {
      toast({ title: "Could not submit request", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  if (!pricing) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="equipment-page">
      {requests.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-slate-900">Your equipment requests</h3>
          <div className="mt-3 space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 p-3" data-testid={`equip-request-${r.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">{r.equipment_type === "complete_setup" ? "Complete Computer Setup" : `${(r.selected_items || []).length} item(s)`}</div>
                  <div className="text-xs text-slate-400">Requested {new Date(r.created_at).toLocaleDateString()} · ${r.equipment_type === "complete_setup" ? r.package_price : r.regular_total}{r.updated_at && r.updated_at !== r.created_at ? ` · Updated ${new Date(r.updated_at).toLocaleDateString()}` : ""}</div>
                  {r.tracking_number && <div className="text-xs font-medium text-violet-700" data-testid={`equip-tracking-${r.id}`}>Tracking: {r.carrier} {r.tracking_number}</div>}
                  <div className="text-xs text-slate-400">Ships to {r.shipping_address}, {r.city}, {r.state} {r.zip}</div>
                </div>
                <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[r.status] || "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`equip-status-${r.id}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="relative overflow-hidden">
        <div className="absolute right-0 top-0 rounded-bl-2xl bg-violet-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white">Best Value</div>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100"><PackageCheck className="h-5 w-5 text-violet-600" /></div>
          <div>
            <h2 className="text-base font-bold text-slate-900">Complete Computer Setup</h2>
            <p className="text-sm text-slate-500">Everything you need to get started, in one package.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-6">
          <div><div className="text-xs uppercase tracking-wide text-slate-400">Complete Setup</div><div className="text-3xl font-extrabold text-violet-600" data-testid="package-price">${pricing.package_price}</div></div>
          <div><div className="text-xs uppercase tracking-wide text-slate-400">Regular Value</div><div className="text-xl font-semibold text-slate-400 line-through" data-testid="regular-value">${pricing.regular_total}</div></div>
          <div><div className="text-xs uppercase tracking-wide text-slate-400">You Save</div><div className="text-xl font-bold text-emerald-600" data-testid="savings">${pricing.savings}</div></div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {pricing.items.map((i) => (
            <div key={i.item_key} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <span className="text-slate-600">{i.label}</span><span className="font-medium text-slate-400">${i.price}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-400">This is a request — you will not be charged in the app. Campbell Communications uses the ${pricing.package_price} package price when arranging your setup.</p>
      </Card>

      <Card>
        <h2 className="text-base font-bold text-slate-900">Request Equipment</h2>
        <p className="text-sm text-slate-500">Don't have the equipment you need? Submit a request and Campbell Communications will contact you regarding your equipment needs.</p>

        {pricing.link?.url && (
          <a href={pricing.link.url} target="_blank" rel="noopener noreferrer" data-testid="equipment-link"
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100">
            <LinkIcon className="h-4 w-4" /> {pricing.link.label || "Open equipment order link"}
          </a>
        )}

        <div className="mt-4 flex gap-2" data-testid="equip-mode-toggle">
          <button type="button" onClick={() => setMode("complete_setup")} className={`rounded-lg border px-4 py-2 text-sm font-semibold ${mode === "complete_setup" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600"}`} data-testid="mode-complete">Complete Setup — ${pricing.package_price}</button>
          <button type="button" onClick={() => setMode("individual")} className={`rounded-lg border px-4 py-2 text-sm font-semibold ${mode === "individual" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600"}`} data-testid="mode-individual">Individual Items</button>
        </div>

        {mode === "individual" && (
          <div className="mt-4 space-y-2">
            {pricing.items.map((i) => (
              <label key={i.item_key} className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5" data-testid={`item-${i.item_key}`}>
                <span className="flex items-center gap-3 text-sm text-slate-700">
                  <input type="checkbox" checked={selected.includes(i.item_key)} onChange={() => toggleItem(i.item_key)} className="h-4 w-4 accent-violet-600" data-testid={`item-check-${i.item_key}`} />
                  {i.label}
                </span>
                <span className="text-sm font-medium text-slate-500">${i.price}</span>
              </label>
            ))}
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
              <span>Selected total</span><span data-testid="individual-total">${indivTotal}</span>
            </div>
            {selected.length === pricing.items.length && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                Tip: choosing the Complete Setup (${pricing.package_price}) instead saves you ${pricing.savings} vs the ${pricing.regular_total} itemized total.
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="eq-name" required /></div>
          <div className="space-y-1.5"><Label>Email address</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="eq-email" required /></div>
          <div className="space-y-1.5"><Label>Phone number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="eq-phone" required /></div>
          <div className="space-y-1.5">
            <Label>Preferred contact method</Label>
            <Select value={form.preferred_contact_method} onValueChange={(v) => setForm({ ...form, preferred_contact_method: v })}>
              <SelectTrigger data-testid="eq-contact-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Phone">Phone</SelectItem><SelectItem value="Email">Email</SelectItem><SelectItem value="Either">Either</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Shipping address</Label><Input value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} data-testid="eq-address" required /></div>
          <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="eq-city" required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="eq-state" required /></div>
            <div className="space-y-1.5"><Label>ZIP</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="eq-zip" required /></div>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Additional notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="eq-notes" placeholder="Anything important about your equipment needs" /></div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy} data-testid="submit-equipment-btn" className="btn-primary-gradient gap-2 border-0 text-white"><Send className="h-4 w-4" /> {busy ? "Submitting…" : "Submit Equipment Request"}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
